import { Formik, Form } from 'formik';
import type { FormikValues } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { PreferredUnitSystem } from '@frontend/api';
import {
  Box,
  Button,
  Center,
  Flex,
  Divider,
  DrawerBody,
  DrawerFooter,
  Spinner,
  Tooltip,
} from '@frontend/legacy-components';

import {
  FormHeader,
  NestSettings,
  PricingSettings,
  StockProperties,
} from 'modules/common/components/nestingDrawers';
import type { LinearNestingDefaultsData } from 'modules/common/components/nestingDrawers';
import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useFeatureFlagValue } from 'store/session/hooks';
import { getGroupId, getPreferredUnitSystem } from 'store/session/selectors';

import { useLinearNestingDefaults, useUpdateLinearNestingDefaults } from './';
import { drawer } from './data/actions';

// Update formik values to match format for POST payload
const getNestFormPayload = (values: FormikValues) => {
  return {
    ...(values as LinearNestingDefaultsData),
    tubeLength: values.tubeLength,
    costDollars: values.costDollars ? Number(values.costDollars) : null,
    partToPartClearance: values.partToPartClearance,
    cuttingToolWidth: values.cuttingToolWidth,
    feedWindow: values.feedWindow ? Number(values.feedWindow) : null,
    dropThresholdPct: values.dropThresholdPct
      ? Number(values.dropThresholdPct)
      : null,
  };
};

export const LinearNestingDefaultsForm = () => {
  const dispatch = useDispatch();
  const locator = useSelector(getGroupId);
  const unitSystem: PreferredUnitSystem = useSelector(getPreferredUnitSystem);
  const handleClose = () => {
    dispatch(drawer.close());
  };
  const { data, isLoading: isCurrentDefaultsLoading } =
    useLinearNestingDefaults({ locator }, {});

  const { mutate: updateLinearNestDefaults, isLoading: isSubmitting } =
    useUpdateLinearNestingDefaults({ locator }, {});

  const isErp = useFeatureFlagValue(featureFlagKeys.isErpCodeAvailable);

  if (isCurrentDefaultsLoading || !data) {
    return (
      <Center h="100%">
        <Spinner size="md" />
      </Center>
    );
  }

  const processedData = {
    ...data,
    costDollars: data.costDollars?.toString() ?? '',
    dropThresholdPct: data.dropThresholdPct?.toString() ?? '',
  };

  return (
    <>
      <Formik
        validationSchema={() =>
          Yup.lazy(() => {
            return Yup.object().shape({
              tubeLength: Yup.string().nullable(),
              erpCode: Yup.string(),
              costDollars: Yup.string(),
              costingUnit: Yup.string().required(),
              nestBehaviorCommonLine: Yup.boolean().required(),
              nestBehaviorUseFeedWindow: Yup.boolean().required(),
              partToPartClearance: Yup.string().nullable(),
              cuttingToolWidth: Yup.string().nullable(),
              feedWindow: Yup.string().nullable(),
              costDistMethod: Yup.string(),
              dropThresholdPct: Yup.string(),
            });
          })
        }
        initialValues={processedData}
        onSubmit={async (values) => {
          const payload = getNestFormPayload(values);
          updateLinearNestDefaults(payload);
        }}
        validateOnMount
        enableReinitialize
      >
        {(formik) => {
          return (
            <Box
              as={Form}
              minH={0}
              display={'flex'}
              flexDir={'column'}
              flexGrow={1}
            >
              <FormHeader isLinearNest />
              <DrawerBody flexGrow={1} minH={0} overflowY={'auto'}>
                <StockProperties
                  isSubmitting={isSubmitting}
                  isErp={isErp}
                  isLinearNest
                  unitSystem={unitSystem}
                />
                <Divider
                  orientation="horizontal"
                  variant="solid"
                  marginTop={3}
                  marginBottom={2}
                />
                <NestSettings
                  isSubmitting={isSubmitting}
                  isLinearNest={true}
                  initialPartToPartClearance={processedData.partToPartClearance}
                  initialFeedWindow={processedData.feedWindow}
                  unitSystem={unitSystem}
                />
                <Divider
                  orientation="horizontal"
                  variant="solid"
                  marginTop={3}
                  marginBottom={2}
                />
                <PricingSettings isSubmitting={isSubmitting} isLinearNest />
              </DrawerBody>
              <DrawerFooter
                gap={1}
                flexDirection="column"
                alignItems="flex-end"
              >
                <Flex gap={1}>
                  <Button
                    size="lg"
                    variant="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      handleClose();
                    }}
                    isDisabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Tooltip
                    isDisabled={isSubmitting || formik.dirty}
                    hasArrow
                    label="No changes to save"
                    placement="top"
                  >
                    <Button
                      colorScheme="default"
                      size="lg"
                      variant="primary"
                      type="submit"
                      isDisabled={!formik.isValid || !formik.dirty}
                      isLoading={isSubmitting}
                    >
                      Save Changes
                    </Button>
                  </Tooltip>
                </Flex>
              </DrawerFooter>
            </Box>
          );
        }}
      </Formik>
    </>
  );
};
